import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import React, { Suspense } from 'react';
import { css } from '@emotion/react';
import { text } from '@guardian/src-foundations/palette';
import { headline } from '@guardian/src-foundations/typography';
import { from } from '@guardian/src-foundations/mq';
import { initPerf } from '@root/src/web/browser/initPerf';
import { AdSlot, labelStyles } from '@root/src/web/components/AdSlot';
import { Lazy } from '@root/src/web/components/Lazy';
import { useAB } from '@guardian/ab-react';
import { abTestTest } from '@frontend/web/experiments/tests/ab-test-test';
import { ArticleDesign } from '@guardian/libs';
import { decidePalette } from '@root/src/web/lib/decidePalette';
import { Hide } from '../../Hide';
import { LeftColumn } from '../../LeftColumn';
const MostViewedFooterData = React.lazy(() => {
    const { start, end } = initPerf('MostViewedFooterData');
    start();
    return import(
    /* webpackChunkName: "MostViewedFooterData" */ './MostViewedFooterData').then((module) => {
        end();
        return { default: module.MostViewedFooterData };
    });
});
const stackBelow = (breakpoint) => css `
	display: flex;
	flex-direction: column;

	${from[breakpoint]} {
		flex-direction: row;
	}
`;
const headingStyles = css `
	${headline.xsmall()};
	color: ${text.primary};
	font-weight: 900;
	padding-right: 5px;
	padding-bottom: 14px;
	padding-top: 3px;

	${from.leftCol} {
		${headline.xsmall()};
		font-weight: 900;
	}

	${from.wide} {
		font-weight: 900;
	}
`;
const adSlotUnspecifiedWidth = css `
	.ad-slot {
		margin: 12px auto;
		min-width: 300px;
		min-height: 274px;
		text-align: center;
	}
`;
const mostPopularAdStyle = css `
	.ad-slot--mostpop {
		width: 300px;
		margin: 12px auto;
		min-width: 300px;
		min-height: 274px;
		text-align: center;
		${from.desktop} {
			margin: 0;
			width: auto;
		}
	}
	${labelStyles};
`;
export const MostViewedFooter = ({ sectionName, format, ajaxUrl }) => {
    // Example usage of AB Tests
    // Used in the Cypress tests as smoke test of the AB tests framework integration
    const ABTestAPI = useAB();
    const abTestCypressDataAttr = (ABTestAPI.isUserInVariant('AbTestTest', 'control') &&
        'ab-test-control') ||
        (ABTestAPI.isUserInVariant('AbTestTest', 'variant') &&
            'ab-test-variant') ||
        'ab-test-not-in-test';
    const runnableTest = ABTestAPI.runnableTest(abTestTest);
    const variantFromRunnable = (runnableTest && runnableTest.variantToRun.id) || 'not-runnable';
    const palette = decidePalette(format);
    return (_jsx("div", Object.assign({ "data-print-layout": "hide", className: "content-footer", css: adSlotUnspecifiedWidth }, { children: _jsxs("div", Object.assign({ css: [stackBelow('leftCol'), mostPopularAdStyle], "data-link-name": "most-popular", "data-component": "most-popular", "data-cy-ab-user-in-variant": abTestCypressDataAttr, "data-cy-ab-runnable-test": variantFromRunnable }, { children: [_jsx(LeftColumn, Object.assign({ size: format.design === ArticleDesign.LiveBlog ||
                        format.design === ArticleDesign.DeadBlog
                        ? 'wide'
                        : 'compact' }, { children: _jsx("h2", Object.assign({ css: headingStyles }, { children: "Most popular" }), void 0) }), void 0), _jsxs("section", Object.assign({ css: stackBelow('desktop') }, { children: [_jsxs("div", Object.assign({ css: stackBelow('leftCol') }, { children: [_jsx(Hide, Object.assign({ when: "above", breakpoint: "leftCol" }, { children: _jsx("h2", Object.assign({ css: headingStyles }, { children: "Most popular" }), void 0) }), void 0), _jsx(Lazy, Object.assign({ margin: 300 }, { children: _jsx(Suspense, Object.assign({ fallback: _jsx(_Fragment, {}, void 0) }, { children: _jsx(MostViewedFooterData, { sectionName: sectionName, palette: palette, ajaxUrl: ajaxUrl }, void 0) }), void 0) }), void 0)] }), void 0), _jsx("div", Object.assign({ css: css `
							margin: 6px 0 0 10px;
						` }, { children: _jsx(AdSlot, { position: "mostpop", display: format.display }, void 0) }), void 0)] }), void 0)] }), void 0) }), void 0));
};
